import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import { post, type ApiError } from '../../lib/api';
import { pluralise, shortDate } from '../../lib/format';
import { Dialog } from '../../components/dialog';
import { useToast } from '../../components/toast';
import { Badge, Button, Field, Input } from '../../components/ui';
import { refreshPeople } from './cache';
import { availabilitySummary, explain } from './labels';
import { PanelSection } from './section';
import type { AvailabilityInput } from './types';

/**
 * Whether an agent is around to be given work, and how much.
 *
 * Availability gates automatic assignment, and nothing in the product could
 * set it: a supervisor could see on the workload screen that an agent was on
 * leave and could not stop round robin handing them tickets, and the agent had
 * no "I'm away" of their own. The server accepted both all along.
 *
 * "Away until" is a scheduled absence that ends by itself at the time given —
 * the router picks the agent up again then, with nothing to remember to undo.
 */

export interface AvailabilityState {
  is_available: boolean | null;
  unavailable_until: string | null;
  unavailable_reason: string | null;
  max_open_tickets: number | null;
  capacity_count?: number;
}

export function AvailabilitySection({
  person,
  editable,
  isSelf = false,
}: {
  person: AvailabilityState & { id: number; full_name: string };
  editable: boolean;
  isSelf?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const summary = availabilitySummary(person);

  return (
    <PanelSection
      title="Availability"
      description="Agents who are away keep their tickets but aren’t given new ones."
      action={
        editable && !editing ? (
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            Change
          </Button>
        ) : undefined
      }
    >
      {editing ? (
        <AvailabilityForm
          agentId={person.id}
          name={person.full_name}
          initial={person}
          isSelf={isSelf}
          onDone={() => setEditing(false)}
        />
      ) : (
        <div className="space-y-1.5">
          <Badge tone={summary.away ? 'caution' : 'positive'} dot>
            {summary.away ? 'Away' : 'Available'}
          </Badge>
          {summary.away && (person.unavailable_until || person.unavailable_reason) && (
            <p className="text-body text-ink">
              {person.unavailable_until
                ? `Back ${shortDate(person.unavailable_until)}`
                : 'Until somebody changes it'}
              {person.unavailable_reason && (
                <>
                  {' · '}
                  <bdi>{person.unavailable_reason}</bdi>
                </>
              )}
            </p>
          )}
          <p className="text-mini text-ink-muted">
            {person.max_open_tickets
              ? `Given at most ${pluralise(person.max_open_tickets, 'ticket')} with a running clock at a time${
                  typeof person.capacity_count === 'number' ? ` (${person.capacity_count} now)` : ''
                }.`
              : 'No limit on how many tickets they’re given.'}
          </p>
        </div>
      )}
    </PanelSection>
  );
}

/** `datetime-local` wants local wall-clock time without an offset. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
}

export function AvailabilityForm({
  agentId,
  name,
  initial,
  onDone,
  isSelf = false,
}: {
  agentId: number;
  name: string;
  initial: AvailabilityState;
  onDone: () => void;
  /** Words the confirmation for the person setting their own. */
  isSelf?: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const ids = useId();
  const [available, setAvailable] = useState(initial.is_available !== false);
  const [reason, setReason] = useState(initial.unavailable_reason ?? '');
  const [until, setUntil] = useState(toLocalInput(initial.unavailable_until));
  const [ceiling, setCeiling] = useState(initial.max_open_tickets ? String(initial.max_open_tickets) : '');

  const save = useMutation<unknown, ApiError, AvailabilityInput>({
    mutationFn: (body) => post(`/users/${agentId}/availability`, body),
    onSuccess: (_, body) => {
      refreshPeople(queryClient, [agentId]);
      const who = isSelf ? 'You’re' : `${name} is`;
      toast.success(
        body.is_available
          ? `${who} available`
          : body.unavailable_until
            ? `${who} away until ${shortDate(body.unavailable_until)}`
            : `${who} away until ${isSelf ? 'you change it' : 'somebody changes it'}`,
      );
      onDone();
    },
  });

  const errors = save.error?.fieldErrors ?? {};
  const general = save.error && Object.keys(errors).length === 0 ? explain(save.error) : null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const untilDate = !available && until ? new Date(until) : null;
    save.mutate({
      is_available: available,
      unavailable_until: untilDate && !Number.isNaN(untilDate.getTime()) ? untilDate.toISOString() : null,
      unavailable_reason: available ? null : reason.trim() || null,
      max_open_tickets: ceiling.trim() === '' ? null : Number(ceiling),
    });
  };

  const reasonId = `${ids}-reason`;
  const untilId = `${ids}-until`;
  const ceilingId = `${ids}-ceiling`;

  return (
    <form onSubmit={submit} className="space-y-3" noValidate>
      <fieldset className="space-y-1.5">
        <legend className="text-mini font-medium text-ink-muted">Status</legend>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-1.5 text-body text-ink">
            <input
              type="radio"
              name={`${ids}-status`}
              checked={available}
              onChange={() => setAvailable(true)}
              className="accent-[var(--color-accent)]"
            />
            Available
          </label>
          <label className="flex items-center gap-1.5 text-body text-ink">
            <input
              type="radio"
              name={`${ids}-status`}
              checked={!available}
              onChange={() => setAvailable(false)}
              className="accent-[var(--color-accent)]"
            />
            Away
          </label>
        </div>
      </fieldset>

      {!available && (
        <>
          <Field label="Reason" htmlFor={reasonId} hint="Shown to the team, for example “Annual leave”." error={errors.unavailable_reason}>
            <Input
              id={reasonId}
              value={reason}
              maxLength={255}
              onChange={(event) => setReason(event.target.value)}
              aria-invalid={errors.unavailable_reason ? true : undefined}
              aria-describedby={errors.unavailable_reason ? `${reasonId}-error` : undefined}
            />
          </Field>
          <Field
            label="Back at"
            htmlFor={untilId}
            hint="They’re given work again from this time. Leave empty to stay away until changed."
            error={errors.unavailable_until}
          >
            <Input
              id={untilId}
              type="datetime-local"
              value={until}
              min={toLocalInput(new Date().toISOString())}
              onChange={(event) => setUntil(event.target.value)}
              className="w-56"
              aria-invalid={errors.unavailable_until ? true : undefined}
              aria-describedby={errors.unavailable_until ? `${untilId}-error` : undefined}
            />
          </Field>
        </>
      )}

      <Field
        label="Most tickets at once"
        htmlFor={ceilingId}
        hint="Counts tickets whose clock is running. Leave empty for no limit."
        error={errors.max_open_tickets}
      >
        <Input
          id={ceilingId}
          type="number"
          inputMode="numeric"
          min={1}
          max={500}
          value={ceiling}
          onChange={(event) => setCeiling(event.target.value)}
          className="w-28"
          aria-invalid={errors.max_open_tickets ? true : undefined}
          aria-describedby={errors.max_open_tickets ? `${ceilingId}-error` : undefined}
        />
      </Field>

      {general && (
        <p role="alert" className="text-mini text-critical-ink">
          {general}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" type="submit" loading={save.isPending}>
          Save availability
        </Button>
      </div>
    </form>
  );
}

/** The same form in a dialog, for the workload table and "set my availability". */
export function AvailabilityDialog({
  open,
  onOpenChange,
  agentId,
  name,
  initial,
  isSelf = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentId: number;
  name: string;
  initial: AvailabilityState;
  isSelf?: boolean;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isSelf ? 'Your availability' : `Availability for ${name}`}
      size="sm"
    >
      <AvailabilityForm
        agentId={agentId}
        name={name}
        initial={initial}
        isSelf={isSelf}
        onDone={() => onOpenChange(false)}
      />
    </Dialog>
  );
}
